import { movies } from "@/mocks/movies";
import type { Movie } from "@/types/content";

export const allMovies = movies as unknown as Movie[];

export const getMovieById = (id: string): Movie | undefined =>
  allMovies.find((movie) => movie.id === id);

export const getMoviesByIds = (ids: string[]): Movie[] =>
  ids
    .map((id) => getMovieById(id))
    .filter((movie): movie is Movie => Boolean(movie));

export const getSimilarMovies = (movie: Movie, count = 5): Movie[] =>
  allMovies
    .filter(
      (item) =>
        item.id !== movie.id &&
        (item.type === movie.type ||
          item.genres.some((genre) => movie.genres.includes(genre)))
    )
    .sort((a, b) => b.rating - a.rating)
    .slice(0, count);

export const getLatestMovies = (count = 8): Movie[] =>
  [...allMovies]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, count);

export const getPopularMovies = (count = 8): Movie[] =>
  [...allMovies].sort((a, b) => b.popularity - a.popularity).slice(0, count);

export const getTopRatedMovies = (count = 8): Movie[] =>
  [...allMovies].sort((a, b) => b.rating - a.rating).slice(0, count);

export interface MovieFilters {
  type?: string;
  region?: string;
  year?: string;
  rating?: string;
}

const ratingThreshold = (rating?: string): number => {
  if (rating === "9分以上") return 9;
  if (rating === "8分以上") return 8;
  if (rating === "7分以上") return 7;
  return 0;
};

export const filterMovies = (list: Movie[], filters: MovieFilters): Movie[] => {
  const minRating = ratingThreshold(filters.rating);
  return list.filter((movie) => {
    if (filters.type && filters.type !== "全部" && movie.type !== filters.type)
      return false;
    if (filters.region && filters.region !== "全部" && movie.region !== filters.region)
      return false;
    if (filters.year && filters.year !== "全部" && String(movie.year) !== filters.year)
      return false;
    if (minRating && movie.rating < minRating) return false;
    return true;
  });
};

export type SortKey = "updated" | "rating" | "popularity";

export const sortMovies = (list: Movie[], key: SortKey): Movie[] => {
  const sorted = [...list];
  if (key === "rating") {
    return sorted.sort((a, b) => b.rating - a.rating);
  }
  if (key === "popularity") {
    return sorted.sort((a, b) => b.popularity - a.popularity);
  }
  return sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
};

export const searchMovies = (query: string): Movie[] => {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return [];
  return allMovies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.type,
      movie.region,
      movie.director,
      ...movie.genres,
      ...movie.actors,
      movie.tagline,
      movie.description
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(keyword);
  });
};

export const pageSize = 8;

export const paginate = <T,>(list: T[], page: number, size = pageSize): T[] =>
  list.slice((page - 1) * size, page * size);